import InspectionActivityChart from '@components/Home/InspectionActivityChart';
import InspectionStatusChart from '@components/Home/InspectionStatusChart';
import InspectionTrendChart from '@components/Home/InspectionTrendChart';
import RecentInspectionsList from '@components/Home/RecentInspectionsList';
import DateRangePicker from '@components/Shared/DateRangePicker';
import { type DateRangePreset, narrowestRangeWithData, rangeStartFor } from '@lib/charts/dateRange';
import Header from '@components/Shared/Layout/Header';
import MetricCard from '@components/Shared/MetricCard';
import { useLocalization } from '@lib/localization';
import { type Inspection } from '@lib/spproject/types';
import { Box, Grid2 as Grid, Typography } from '@mui/material';
import { useGetStatisticsQuery } from '@store/api/statisticEndpoints';
import { useLoading } from '@store/hooks';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { AlertTriangle, BarChart3, FileText, Target, TrendingDown, TrendingUp } from 'lucide-react';
import { type FC, useEffect, useId, useState } from 'react';
import { ClipboardIcon, FolderIcon } from 'react-line-awesome';

const HomePage: FC = () => {
  // Null until the reader picks one, so the range follows the data as it loads in.
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset | null>(null);
  const { data, isLoading } = useGetStatisticsQuery();
  const { show, hide } = useLoading();
  const componentId = useId();
  const noticeColors = useNoticeColors();

  const { t } = useLocalization();

  useEffect(() => {
    (isLoading ? show : hide)(componentId);

    return () => hide(componentId);
  }, [componentId, hide, isLoading, show]);

  const dailyInspectionData = (data?.dailyInspections ?? []).map((inspection) => ({
    date: new Date(inspection.dateModified),
    total: inspection.total ?? 0,
    ok: inspection.status?.Ok ?? 0,
    notChecked: inspection.status?.NotChecked ?? 0,
    warning: inspection.status?.Warning ?? 0,
    error: inspection.status?.Error ?? 0,
  }));

  const statusTotals = dailyInspectionData.reduce(
    (acc, d) => {
      acc.Ok += d.ok;
      acc.NotChecked += d.notChecked;
      acc.Warning += d.warning;
      acc.Error += d.error;
      return acc;
    },
    { Ok: 0, NotChecked: 0, Warning: 0, Error: 0 } as Record<Inspection.NoticeType, number>
  );

  const statusRates = Object.fromEntries(
    Object.entries(statusTotals).map(([noticeType, value]) => [
      noticeType,
      data?.inspectionCount ? (value / data.inspectionCount) * 100 : 0,
    ])
  ) as Record<Inspection.NoticeType, number>;

  const inspectionDates = dailyInspectionData.map((d) => d.date);
  const dateRangePreset = selectedPreset ?? narrowestRangeWithData(inspectionDates);

  const dateRange = { start: rangeStartFor(dateRangePreset), end: new Date() };

  const filteredDailyData = dailyInspectionData.filter(
    (d) => d.date >= dateRange.start && d.date <= dateRange.end
  );

  const filteredStatusTotals = filteredDailyData.reduce(
    (acc, d) => {
      acc.Ok += d.ok;
      acc.NotChecked += d.notChecked;
      acc.Warning += d.warning;
      acc.Error += d.error;
      return acc;
    },
    { Ok: 0, NotChecked: 0, Warning: 0, Error: 0 } as Record<Inspection.NoticeType, number>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ md: 4, xs: 12 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 2, gap: 1 }}>
            <BarChart3 size={24} />
            <Typography variant="h6" sx={{ ml: 1, fontWeight: 300 }}>
              {t('Statistic_ActivityOverview')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <MetricCard
              color="#2196f3"
              icon={<FolderIcon style={{ color: '#2196f3', fontSize: 33 }} />}
              value={data?.projectCount ?? 0}
              label={t('Common_Projects')}
            />
            <MetricCard
              color="#4caf50"
              icon={<Target size={32} style={{ color: '#4caf50' }} />}
              value={data?.inspectionCount ?? 0}
              label={t('Common_Inspections')}
            />
            <MetricCard
              color="#ff9800"
              icon={<ClipboardIcon style={{ color: '#ff9800', fontSize: 33 }} />}
              value={data?.checklistCount ?? 0}
              label={t('Common_Checklists')}
            />
          </Box>
        </Grid>

        <Grid size={{ md: 8, xs: 12 }}>
          <InspectionActivityChart data={dailyInspectionData.map((d) => ({ date: d.date, total: d.total }))} />
        </Grid>
      </Grid>

      <Box sx={{ mb: 2, width: '100%' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              color={noticeColors.Ok}
              icon={<FileText size={32} style={{ color: noticeColors.Ok }} />}
              value={statusTotals.Ok}
              label={t('Common_Successful')}
              progressValue={statusRates.Ok}
              progressLabel={t('Common_Rate_Data', [statusRates.Ok.toFixed(2)])}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              color={noticeColors.NotChecked}
              icon={<Target size={32} style={{ color: noticeColors.NotChecked }} />}
              value={statusTotals.NotChecked}
              label={t('Common_NotChecked')}
              progressValue={statusRates.NotChecked}
              progressLabel={t('Common_Rate_Data', [statusRates.NotChecked.toFixed(2)])}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              color={noticeColors.Warning}
              icon={<BarChart3 size={32} style={{ color: noticeColors.Warning }} />}
              value={statusTotals.Warning}
              label={t('Common_Warning')}
              progressValue={statusRates.Warning}
              progressLabel={t('Common_Rate_Data', [statusRates.Warning.toFixed(2)])}
              bottomIcon={<TrendingUp size={16} style={{ color: '#4caf50', marginRight: 4 }} />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              color={noticeColors.Error}
              icon={<AlertTriangle size={32} style={{ color: noticeColors.Error }} />}
              value={statusTotals.Error}
              label={t('Common_Error')}
              progressValue={statusRates.Error}
              progressLabel={t('Common_Rate_Data', [statusRates.Error.toFixed(2)])}
              bottomIcon={<TrendingDown size={16} style={{ color: '#4caf50', marginRight: 4 }} />}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pl: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChart3 size={24} />
            <Typography variant="h6" sx={{ ml: 1, fontWeight: 300 }}>
              {t('Statistic_InspectionAnalysis')}
            </Typography>
          </Box>
          <DateRangePicker value={dateRangePreset} onChange={setSelectedPreset} dates={inspectionDates} />
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ md: 4, xs: 12 }}>
            <InspectionStatusChart data={filteredStatusTotals} />
          </Grid>
          <Grid size={{ md: 8, xs: 12 }}>
            <InspectionTrendChart data={filteredDailyData} />
          </Grid>
        </Grid>
      </Box>

      <RecentInspectionsList data={data?.recentInspections} />
    </Box>
  );
};

export default HomePage;
